import React, { useState, useMemo, useCallback } from 'react';
import { makeStyles, Container, Grid, Paper } from '@material-ui/core';

import BreadCrumb from 'app/components/BreadCrumb';
import LicenseRegistration from 'app/components/ServicesForms/Licenses/LicenseRegistration';

import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { FormServiceContext } from 'app/utils/contexts';
import { SERVICES_PROPERTIES } from 'app/config/services';
import { URLS } from 'app/config/urls';
import { STATUS_CODES } from 'app/config/status';

import licenseEndpoint from 'app/api/services/licenseEndpoint';
import alertsEndpoint from 'app/api/services/alertsEndpoint';

import notification from 'app/config/notification';
import withRequestInitiation from 'app/components/Hoc/withRequestInitiation';

import { preparePayload } from 'app/utils/forms/functions';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import { fetchSaveRequest, updateRequestDetails } from 'app/store/slices/requests';
import { errorHandler } from 'app/utils/errorHandler';
import { validateLicenseRequest } from 'app/utils/forms/validators/license';

import styles from './styles';
const useStyles = makeStyles(styles);

function Licenses(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const {
        intl,
        loadingDocuments,
        setLoadingDocuments,
        canEdit,
        editing,
        resetAndGo,
        onCancel,
        validateForm,
        breadCrumb,
        rightInfo,
    } = props;

    const [errorsList, setErrorsList] = useState({});

    const documentsStore = useSelector((state) => state.services.documents);
    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const userAdmin = useSelector((state) => state.auth.userAdmin);

    const initialValues = useMemo(() => {
        if (editing) {
            const value = JSON.parse(request.jsonSolicitacao);
            value.nuMeses = String(value.nuMeses || '');
            value.frequencyTest = null;
            return value;
        }
        if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues) {
            return SERVICES_PROPERTIES[service.cdServico].formInitialValues;
        }
        return {};
    }, [editing, request.jsonSolicitacao, service.cdServico]);

    const onSave = useCallback(
        async (values, fromSubmit, isInternalForm) => {
            if (!canEdit) {
                if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
            }

            const valuesCopy = { ...values };

            if (fromSubmit) {
                const errors = validateLicenseRequest({
                    values: valuesCopy,
                    cdServico: service.cdServico,
                    intl,
                    isInternalForm,
                });
                if (Object.keys(errors).length > 0) {
                    setErrorsList(errors);
                    return;
                } else {
                    setErrorsList({});
                }
            }

            const documentsObject = {
                store: documentsStore,
                files: valuesCopy.documentos,
            };

            if (request?.situacao) valuesCopy.situacao = request.situacao;

            let dataToPayload = {
                cdServico: service.cdServico,
            };

            if (request && request.cdServico) {
                dataToPayload = { ...request };
            }

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: documentsObject,
                params: dataToPayload,
                activeCompany,
                userAdmin,
                registrationUserInfo,
            });

            dispatch(showLoading());
            let showMessage = false;
            let response = false;
            if (!fromSubmit) showMessage = true;
            try {
                response = await fetchSaveRequest({
                    isFormData: true,
                    preparedPayload,
                    dispatch,
                    intl,
                    showMessage,
                    cdService: service.cdServico,
                });
                if (response?.status === 200 && response?.data) {
                    await dispatch(updateRequestDetails(response.data));
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            if (!fromSubmit) dispatch(hideLoading());
            return response;
        },
        [
            activeCompany,
            canEdit,
            dispatch,
            documentsStore,
            intl,
            registrationUserInfo,
            request,
            service.cdServico,
            userAdmin,
        ],
    );

    const onSubmit = useCallback(
        async (values) => {
            if (!canEdit) {
                if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
            }

            const currentStatusCode = request.cdSituacao;

            //save before finish
            const responseSave = await onSave(values, true);

            if (responseSave && responseSave.status === 200) {
                try {
                    const response = await licenseEndpoint.finishRequest(responseSave.data.cdSolicitacao);
                    if (response.status === 200) {
                        notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }), {
                            toastId: 'SUCCESSFUL_FINISHED_REQUEST',
                            type: 'success',
                        });

                        if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
                            await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
                        }
                    }
                    resetAndGo();
                } catch (error) {
                    errorHandler(error.response?.status, error.response?.data?.message);
                }
            }
            dispatch(hideLoading());
        },
        [canEdit, dispatch, intl, onSave, request.cdSituacao, request.cdSolicitacao, request.situacao, resetAndGo],
    );

    const contextProvider = useMemo(() => {
        return {
            loadingDocuments,
            setLoadingDocuments,
            validateForm,
            onSubmit,
            onSave,
            onCancel,
            initialValues,
            validationSchema: SERVICES_PROPERTIES[service.cdServico]
                ? SERVICES_PROPERTIES[service.cdServico].validationSchema
                : {},
            canEdit,
            errorsList,
            setErrorsList,
        };
    }, [
        loadingDocuments,
        setLoadingDocuments,
        validateForm,
        onSubmit,
        onSave,
        onCancel,
        initialValues,
        service.cdServico,
        canEdit,
        errorsList,
        setErrorsList,
    ]);

    const handleOnBack = () => {
        if (request.nuSolicitacaoFmt && request.nuSolicitacaoFmt !== '') {
            history.push(URLS.START);
        } else {
            history.push(generatePath(URLS.SERVICES));
        }
    };

    return (
        <Container>
            <BreadCrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper className={classes.container} elevation={3}>
                        <FormServiceContext.Provider value={contextProvider}>
                            <LicenseRegistration {...props} />
                        </FormServiceContext.Provider>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(withRequestInitiation(Licenses));
